import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { SupabaseProvider } from './contexts/SupabaseContext';
import '@/App.css';
import { initFingerprint } from './utils/deviceFingerprint';
import { handleMobileAuthToken, isMobileApp, setMobileAppSession } from './utils/mobileUtils';

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Components
import Navbar from './components/Navbar';
import BottomNav from './components/BottomNav';
import Breadcrumb from './components/Breadcrumb';
import LandingPage from './components/LandingPage';
import AuthModal from './components/AuthModal';
import MobileGoogleSignin from './components/MobileGoogleSignin';
import ProfileModal from './components/ProfileModal';
import Dashboard from './components/Dashboard';
import ExamCategoryPage from './components/ExamCategoryPage';
import JobBoard from './components/JobBoard';
import EmployerDashboard from './components/EmployerDashboard';
import PostJob from './components/PostJob';
import JobDetails from './components/JobDetails';
import Payment from './components/Payment';
import AIAdvisor from './components/AIAdvisor';
import FriendDiscovery from './components/FriendDiscovery';
import FriendsWithChat from './components/FriendsWithChat';
import AdminDashboard from './components/AdminDashboard';
import ImageGenerator from './components/ImageGenerator';
import LearnResearch from './components/LearnResearch';
import Profile from './components/Profile';
import TestPrep from './components/TestPrep';
import TestPrepEnhanced from './components/TestPrepEnhanced';
import TestPrepAdmin from './components/TestPrepAdmin';
import TestTaking from './components/TestTaking';
import ExamBankHub from './components/ExamBankHub';
import ExamBankPractice from './components/ExamBankPractice';
import EngineeringExamBank from './components/EngineeringExamBank';
import PharmacyExamHub from './components/PharmacyExamHub';
import DentistExamHub from './components/DentistExamHub';
import GlobalExamHub from './components/GlobalExamHub';
import FreeTrialExams from './components/FreeTrialExams';
import Events from './components/Events';
import EventDetails from './components/EventDetails';
import EventCheckout from './components/EventCheckout';
import OrganizerDashboard from './components/OrganizerDashboard';
import PostEvent from './components/PostEvent';
import MyEvents from './components/MyEvents';
import PricingPlans from './components/PricingPlans';
import PaymentSuccess from './components/PaymentSuccess';
import ContentProtection from './components/ContentProtection';
import BuyCreditsPage from './components/BuyCreditsPage';
import MermaidDemo from './components/MermaidDemo';
import CareerAmbition from './components/CareerAmbition';
import LowCreditModal from './components/LowCreditModal';
import PromoCodeManager from './components/PromoCodeManager';
import PromoRedemption from './components/PromoRedemption';
import CheckoutPage from './components/CheckoutPage';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import ContactUs from './components/ContactUs';
import DeleteAccount from './components/DeleteAccount';
import FloatingChatWidget from './components/FloatingChatWidget';
import PermissionRequest from './components/PermissionRequest';
import CategoryCourses from './components/CategoryCourses';
import SamharaAI from './components/SamharaAI';
import StudentOffers from './components/StudentOffers';
import AdminOffers from './components/AdminOffers';
import Scholarships from './pages/Scholarships';
import ScholarshipDetail from './pages/ScholarshipDetail';

// Creator AI Page Components
import SocialMediaCreativesPage from './components/creatorai/SocialMediaCreativesPage';
import BulkCreatePage from './components/creatorai/BulkCreatePage';
import ReelsWithAvatarsPage from './components/creatorai/ReelsWithAvatarsPage';
import TextToImagePage from './components/creatorai/TextToImagePage';
import HeyGenHub from './components/creatorai/HeyGenHub';
import VideoAgentPage from './components/creatorai/VideoAgentPage';
import AvatarVideoPage from './components/creatorai/AvatarVideoPage';
import VideoTranslatePage from './components/creatorai/VideoTranslatePage';
import PhotoAvatarPage from './components/creatorai/PhotoAvatarPage';
import DigitalTwinPage from './components/creatorai/DigitalTwinPage';
import AvatarMotionPage from './components/creatorai/AvatarMotionPage';
import SharedVideoPage from './components/creatorai/SharedVideoPage';

// LMS Components
import { CourseCatalog, CourseDetail, MyLearning, SmartCalendar, LMSAdminDashboard, CourseContentManager, BatchSessionManager, LMSPayment, LMSPaymentSuccess, CertificateView, CertificateVerify } from './components/lms';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Setup axios interceptor for device mismatch errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle device mismatch (401 with specific message)
        if (error.response?.status === 401) {
          const message = error.response?.data?.detail || '';
          if (message.includes('another device') || message.includes('device')) {
            // User logged in from another device
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
            setShowAuthModal(true);
            toast.error('Your account was accessed from another device. Please login again.');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Listen for openAuthModal events dispatched from any page (e.g. Scholarships)
  useEffect(() => {
    const handler = () => setShowAuthModal(true);
    window.addEventListener('openAuthModal', handler);
    return () => window.removeEventListener('openAuthModal', handler);
  }, []);

  // ── Google OAuth callback ─────────────────────────────────────────
  // When Google redirects back with #session_id=xxx in the URL, we
  // silently exchange it for a JWT and log the user in — no separate page.
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = window.location.hash;
    if (!hash?.includes('session_id=')) return;

    const params = new URLSearchParams(hash.slice(1));
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    // Clear the hash immediately so it's not visible in the URL
    window.history.replaceState({}, document.title, window.location.pathname);

    const exchangeGoogleSession = async () => {
      try {
        const response = await axios.post(`${API}/auth/google`, { session_id: sessionId });
        const { access_token, user: userData } = response.data;

        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setUser(userData);
        closeAuth();

        // Show profile completion only if NOT on a payment / exam page
        const skipPaths = [
          '/pricing', '/buy-credits', '/student-offers', '/checkout',
          '/payment', '/test-prep', '/test-taking', '/exam-bank',
          '/free-trial-exams', '/promo', '/lms',
          '/events', '/event-checkout', '/my-events',
        ];
        const onSkipPage = skipPaths.some(p => window.location.pathname.startsWith(p));
        if (!onSkipPage && !userData.profile?.completed_questionnaire) {
          setTimeout(() => setShowProfileModal(true), 60000);
        }

        const isNew = !userData.google_linked;
        toast.success(isNew ? 'Welcome to Nerd Nation! 🎉 You have 30 free credits.' : 'Welcome back!');
      } catch (err) {
        const msg = err.response?.data?.detail || 'Google sign-in failed. Please try again.';
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    exchangeGoogleSession();
  }, []); // intentional: run once on mount only
  // ──────────────────────────────────────────────────────────────────

  // Check for existing token on app load
  useEffect(() => {
    // Initialize device fingerprinting
    initFingerprint();
    
    // Add class to body if running as mobile app (for mobile-specific CSS)
    if (isMobileApp()) {
      document.body.classList.add('capacitor-mobile-app');
    }
    
    // ── Mobile App Auto-Login ──────────────────────────────────────
    // When the mobile app opens the website in browser for payments,
    // it passes ?source=mobile_app&token=JWT in the URL.
    // We read it here, store it, and log the user in automatically.
    const urlParams = new URLSearchParams(window.location.search);
    const mobileSource = urlParams.get('source');
    const mobileToken = urlParams.get('token');

    if (mobileSource === 'mobile_app' && mobileToken) {
      // Store token and mark as mobile session
      localStorage.setItem('token', mobileToken);
      setMobileAppSession(true);
      // Clean the token out of the URL so it's not visible / bookmarked
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
    }
    // ──────────────────────────────────────────────────────────────

    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, []);

  // ── Auto-refresh subscription/profile when the mobile app resumes ──
  // After a user pays in the external browser and deep-links back to the app,
  // Android brings the WebView to the foreground. This listener refetches the
  // profile so the new subscription/credits show up immediately.
  useEffect(() => {
    if (!isMobileApp()) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('token')) {
        fetchUserProfile();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // ── Deep-link handler for  com.nerdnation.app://login?token=JWT  ──
  // When the user completes Google login in the external Chrome tab, that page
  // deep-links back here with the JWT. Capacitor's App plugin fires appUrlOpen.
  useEffect(() => {
    if (!isMobileApp()) return;
    let removeListener = () => {};
    (async () => {
      try {
        const { App: CapacitorApp } = await import('@capacitor/app');
        const handle = await CapacitorApp.addListener('appUrlOpen', async (event) => {
          try {
            const raw = event?.url || '';
            // com.nerdnation.app://login?token=XXX
            const match = raw.match(/[?&]token=([^&]+)/);
            if (raw.includes('/login') && match && match[1]) {
              const token = decodeURIComponent(match[1]);
              localStorage.setItem('token', token);
              setMobileAppSession(true);
              axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
              await fetchUserProfile();
              toast.success('Signed in with Google 🎉');
            } else if (raw.includes('/payment-complete')) {
              // Just refresh so the newly-purchased plan / credits show up
              if (localStorage.getItem('token')) {
                await fetchUserProfile();
              }
            }
          } catch (e) {
            console.error('Deep-link handler error:', e);
          }
        });
        removeListener = () => handle.remove();
      } catch (e) {
        // @capacitor/app not available (web preview) — safe to ignore.
      }
    })();
    return () => { try { removeListener(); } catch (e) { /* noop */ } };
  }, []);

  // Disable right-click and text selection only on protected pages (exam pages ONLY)
  useEffect(() => {
    // Check if current page should have right-click disabled
    const isProtectedPage = () => {
      const path = window.location.pathname;
      // Protect ONLY exam-related pages
      const protectedPaths = [
        '/exam-bank',
        '/dentist-exam-bank',
        '/pharmacy-exam-bank',
        '/engineering-exam-bank',
        '/global-exam-bank',
        '/free-trial-exams'
      ];
      return protectedPaths.some(p => path.startsWith(p));
    };

    // Disable right-click context menu only on protected pages
    const disableContextMenu = (e) => {
      if (isProtectedPage()) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection via keyboard shortcuts only on protected pages
    const disableKeyboardShortcuts = (e) => {
      if (isProtectedPage()) {
        // Ctrl+C, Ctrl+A, Ctrl+X, Ctrl+U, F12 (DevTools)
        if (
          (e.ctrlKey && (e.key === 'c' || e.key === 'a' || e.key === 'x' || e.key === 'u')) ||
          e.key === 'F12'
        ) {
          e.preventDefault();
          return false;
        }
      }
    };

    document.addEventListener('contextmenu', disableContextMenu);
    document.addEventListener('keydown', disableKeyboardShortcuts);

    return () => {
      document.removeEventListener('contextmenu', disableContextMenu);
      document.removeEventListener('keydown', disableKeyboardShortcuts);
    };
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/me`);
      setUser(response.data);
      
      // Check if profile is incomplete - show modal for profile completion
      // Skip on payment, exam, and offer pages so users can purchase without friction
      const skipProfilePaths = [
        '/pricing', '/buy-credits', '/student-offers', '/checkout',
        '/payment', '/test-prep', '/test-taking', '/exam-bank',
        '/dentist-exam-bank', '/pharmacy-exam-bank', '/engineering-exam-bank',
        '/global-exam-bank', '/free-trial-exams', '/promo', '/lms',
      ];
      const onSkipPage = skipProfilePaths.some(p => window.location.pathname.startsWith(p));

      if (!onSkipPage && !response.data.profile?.completed_questionnaire) {
        // Check if user has placeholder values from quick signup
        const hasPlaceholderData = 
          response.data.profile?.college_name === "To be filled" ||
          response.data.profile?.degree_name === "To be filled";
        
        // Show modal if profile is incomplete OR has placeholder data
        if (!response.data.profile?.college_name || 
            !response.data.profile?.degree_name || 
            hasPlaceholderData) {
          setShowProfileModal(true);
        }
      }
      // Return user data for immediate use
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (formData, isRegister) => {
    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      const { access_token, user: userData } = response.data;
      
      // Store token and set auth header
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userData);
      closeAuth();
      
      // For new signups, show profile modal ONLY if not on checkout page
      // Profile modal will be shown after payment for checkout users
      const isOnCheckoutPage = window.location.pathname.includes('/checkout/');
      if (isRegister && !isOnCheckoutPage) {
        setShowProfileModal(true);
      }
      
      toast.success(isRegister ? 'Registration successful! You can now proceed with payment.' : 'Login successful!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
      throw error;
    }
  };

  const handleProfileComplete = async (profileData) => {
    try {
      await axios.post(`${API}/profile/complete`, profileData);
      
      // Update user state
      setUser(prev => ({
        ...prev,
        profile: { ...profileData, completed_questionnaire: true }
      }));
      
      setShowProfileModal(false);
      toast.success('Profile completed! You can now use the AI advisor.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to complete profile';
      toast.error(message);
      throw error;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Logged out successfully');
  };

  const openAuth = (mode = 'login') => {
    setAuthMode(mode);
    setShowAuthModal(true);
    // Dispatch event to notify BottomNav
    window.dispatchEvent(new CustomEvent('openAuthModal'));
  };

  const closeAuth = () => {
    setShowAuthModal(false);
    // Dispatch event to notify BottomNav
    window.dispatchEvent(new CustomEvent('closeAuthModal'));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const handleLogin = async (userData, token, showProfile = false) => {
    // Store token and set auth header
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    setUser(userData);
    
    // Show profile modal after 60s if requested (e.g., after payment for new users)
    // Delay gives the user time to explore before being interrupted
    if (showProfile && !userData.profile?.completed_questionnaire) {
      setTimeout(() => {
        setShowProfileModal(true);
      }, 60000);
    }
  };

  // ── Protected Route ────────────────────────────────────────────────
  // Waits for auth to finish before deciding to redirect.
  // Using `replace` on redirect was corrupting browser history:
  // during loading, user=null → replace fired → back button broke.
  const ProtectedRoute = ({ children, condition }) => {
    const allowed = condition !== undefined ? condition : !!user;
    if (loading) return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
    if (!allowed) return <Navigate to="/" />;
    return children;
  };
  // ──────────────────────────────────────────────────────────────────

  return (
    <HelmetProvider>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <SupabaseProvider>
      <AuthContext.Provider value={{ user, setUser, handleAuth, handleLogin, handleLogout, openAuth, fetchUser: fetchUserProfile }}>
        <div className="App">
          <ContentProtection user={user} />
          {/* Offline Banner */}
          {!isOnline && (
            <div data-testid="offline-banner" style={{
              position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
              background: '#b45309', color: '#fff', textAlign: 'center',
              padding: '8px 16px', fontSize: '14px', fontWeight: '500'
            }}>
              No internet connection — some features are unavailable
            </div>
          )}
          <BrowserRouter>
            <ScrollToTop />
            <Navbar />
            {user && <Breadcrumb />}
          
          <Routes>
            {/* Dashboard is now the home page - accessible to everyone */}
            <Route 
              path="/" 
              element={<Dashboard />} 
            />

            {/* Exam category pages */}
            <Route
              path="/exam-preparation/:category"
              element={<ExamCategoryPage />}
            />
            
            {/* Old landing page moved to /about */}
            <Route path="/about" element={<LandingPage />} />
            
            {/* AI Advisor - accessible to everyone */}
            <Route path="/ai-advisor" element={<AIAdvisor />} />
            
            {/* Learn Research - accessible to everyone */}
            <Route path="/learn-research" element={<LearnResearch />} />
            
            {/* Test Prep - requires auth */}
            <Route path="/test-prep" element={<TestPrepEnhanced />} />
            <Route path="/test-taking/:testId" element={<ProtectedRoute><TestTaking /></ProtectedRoute>} />
            <Route path="/test-prep-admin" element={<ProtectedRoute><TestPrepAdmin /></ProtectedRoute>} />
            
            {/* Exam Banks */}
            <Route path="/exam-bank" element={<ExamBankHub />} />
            <Route path="/exam-bank/:examId" element={<ExamBankPractice />} />
            <Route path="/engineering-exam-bank" element={<EngineeringExamBank />} />
            <Route path="/pharmacy-exam-bank" element={<PharmacyExamHub />} />
            <Route path="/dentist-exam-bank" element={<DentistExamHub />} />
            <Route path="/free-trial-exams" element={<FreeTrialExams />} />
            <Route path="/global-exam-bank" element={<GlobalExamHub />} />

            <Route path="/pricing" element={<PricingPlans />} />
            <Route path="/mobile-google-signin" element={<MobileGoogleSignin />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/buy-credits" element={<ProtectedRoute><BuyCreditsPage /></ProtectedRoute>} />
            <Route path="/mermaid-demo" element={<MermaidDemo />} />
            <Route path="/career-ambition" element={<CareerAmbition />} />
            <Route path="/checkout/:planId" element={<CheckoutPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/contact-us" element={<ContactUs />} />

            <Route path="/friends" element={<ProtectedRoute><FriendsWithChat /></ProtectedRoute>} />
            <Route path="/image-generator" element={<ImageGenerator />} />
            <Route path="/creator-ai/social-creatives" element={<ProtectedRoute><SocialMediaCreativesPage /></ProtectedRoute>} />
            <Route path="/creator-ai/bulk-create" element={<ProtectedRoute><BulkCreatePage /></ProtectedRoute>} />
            <Route path="/creator-ai/reels-avatar" element={<ProtectedRoute><ReelsWithAvatarsPage /></ProtectedRoute>} />
            <Route path="/creator-ai/heygen-hub" element={<ProtectedRoute><HeyGenHub /></ProtectedRoute>} />
            <Route path="/creator-ai/video-agent" element={<ProtectedRoute><VideoAgentPage /></ProtectedRoute>} />
            <Route path="/creator-ai/avatar-video" element={<ProtectedRoute><AvatarVideoPage /></ProtectedRoute>} />
            <Route path="/creator-ai/video-translate" element={<ProtectedRoute><VideoTranslatePage /></ProtectedRoute>} />
            <Route path="/creator-ai/photo-avatar" element={<ProtectedRoute><PhotoAvatarPage /></ProtectedRoute>} />
            <Route path="/creator-ai/digital-twin" element={<ProtectedRoute><DigitalTwinPage /></ProtectedRoute>} />
            <Route path="/creator-ai/avatar-motion" element={<ProtectedRoute><AvatarMotionPage /></ProtectedRoute>} />
            <Route path="/shared-video/:token" element={<SharedVideoPage />} />
            <Route path="/creator-ai/text-to-image" element={<ProtectedRoute><TextToImagePage /></ProtectedRoute>} />
            <Route path="/samhara-ai" element={<SamharaAI />} />

            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/job-board" element={<JobBoard />} />
            {/* Events Module */}
            <Route path="/events" element={<Events />} />
            <Route path="/events/:eventId" element={<EventDetails />} />
            <Route path="/event-checkout/:eventId" element={<ProtectedRoute><EventCheckout /></ProtectedRoute>} />
            <Route path="/my-events" element={<ProtectedRoute><MyEvents /></ProtectedRoute>} />
            <Route path="/organizer-dashboard" element={<ProtectedRoute><OrganizerDashboard /></ProtectedRoute>} />
            <Route path="/post-event" element={<ProtectedRoute><PostEvent /></ProtectedRoute>} />
            <Route path="/employer-dashboard" element={<ProtectedRoute><EmployerDashboard /></ProtectedRoute>} />
            <Route path="/post-job" element={<ProtectedRoute><PostJob /></ProtectedRoute>} />
            <Route path="/jobs/:jobId" element={<JobDetails />} />

            {/* Redirect old chat route to friends page */}
            <Route path="/chat" element={<Navigate to="/friends" />} />

            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/promos" element={<PromoCodeManager />} />
            <Route path="/promo/:code" element={<PromoRedemption user={user} />} />
            <Route path="/payment" element={<Payment />} />
            
            {/* Category Course Pages */}
            <Route path="/category/:categorySlug" element={<CategoryCourses />} />
            
            {/* LMS Routes */}
            <Route path="/courses" element={<CourseCatalog />} />
            <Route path="/courses/:courseId" element={<CourseDetail />} />
            <Route path="/my-learning" element={<ProtectedRoute><MyLearning /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><SmartCalendar /></ProtectedRoute>} />
            <Route path="/lms-admin" element={<ProtectedRoute condition={user?.lms_role === 'admin' || user?.lms_role === 'sub_admin'}><LMSAdminDashboard /></ProtectedRoute>} />
            <Route path="/lms-admin/courses/:courseId" element={<ProtectedRoute condition={user?.lms_role === 'admin' || user?.lms_role === 'sub_admin'}><CourseContentManager /></ProtectedRoute>} />
            <Route path="/lms-admin/batches/:batchId" element={<ProtectedRoute condition={user?.lms_role === 'admin' || user?.lms_role === 'sub_admin'}><BatchSessionManager /></ProtectedRoute>} />
            <Route path="/lms/payment/:enrollmentId" element={<ProtectedRoute><LMSPayment /></ProtectedRoute>} />
            <Route path="/lms/payment-success" element={<ProtectedRoute><LMSPaymentSuccess /></ProtectedRoute>} />
            <Route path="/certificates/:certificateId" element={<CertificateView />} />
            <Route path="/verify-certificate/:certificateNumber" element={<CertificateVerify />} />
            <Route path="/verify-certificate" element={<CertificateVerify />} />
            
            {/* Student Offers */}
            <Route path="/student-offers" element={<ProtectedRoute><StudentOffers /></ProtectedRoute>} />
            <Route path="/admin-offers" element={<ProtectedRoute condition={user?.email === 'shifracs@gmail.com'}><AdminOffers /></ProtectedRoute>} />

            {/* Scholarships */}
            <Route path="/scholarships" element={<Scholarships />} />
            <Route path="/scholarships/:idOrSlug" element={<ScholarshipDetail />} />

            {/* Delete Account */}
            <Route path="/delete-account" element={<ProtectedRoute><DeleteAccount /></ProtectedRoute>} />
          </Routes>

          {/* Modals */}
          {showAuthModal && (
            <AuthModal
              mode={authMode}
              onClose={closeAuth}
              onAuth={handleAuth}
              onSwitchMode={(mode) => setAuthMode(mode)}
            />
          )}

          {showProfileModal && user && (
            <ProfileModal
              onComplete={handleProfileComplete}
              onSkip={() => setShowProfileModal(false)}
            />
          )}

          {/* Low Credit Warning Modal - Don't show on Career Ambition page */}
          <LowCreditModal user={user} excludePaths={['/career-ambition']} />

          {/* Floating Chat Widget - Always visible for logged in users */}
          <FloatingChatWidget />

          {/* Request device permissions once on first visit */}
          <PermissionRequest />

          <Toaster position="top-right" />
          <BottomNav />
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
    </SupabaseProvider>
    </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export { AuthContext };
export default App;

